import { useState, useEffect, useRef } from 'react';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { useTranslation } from 'react-i18next';
import { encode } from '@/base64';
import { getCharForNumber } from '@/util';
import { IconButton } from '@teo/components';
import { ArrowLeft, ArrowRight, CrossFilled } from '@teo/components/icons';

export const pastels = [
  '#C25FF4',
  '#E3BF00',
  '#6EC6C0',
  '#F25850',
  '#29BAFF',
  '#AE7226',
  '#1173DD',
  '#9AD055',
  '#807EFF',
  '#FF8EA4',
];

const PopupInfo = ({
  pastels,
  labels,
  setOpenPopup,
  openPopup,
  setActiveItem,
  activeItem,
  registerPopup,
  getCharForNumber,
}) => {
  if (!openPopup || !activeItem?.uid) return null;

  const index = labels.findIndex((i) => i.uid === activeItem.uid);
  if (index < 0) return null;

  const x = activeItem;

  return (
    <div ref={(el) => registerPopup(x.uid, el)}>
      <div
        className={`absolute top-4 z-[2] flex min-w-[264px] flex-col rounded-lg bg-white py-4 drop-shadow-m ${
          x.x * 100 >= 50 ? 'left-4' : 'right-4'
        }`}
      >
        <div className="flex w-full flex-row items-center px-4">
          <div
            style={{ backgroundColor: pastels[index] }}
            className="mr-2 rounded-md px-2.5 pb-1 pt-0.5 text-white"
          >
            {getCharForNumber(index)}
          </div>

          <div className="ml-auto">
            <IconButton
              className="!text-grey-07"
              size="sm"
              Icon={CrossFilled}
              variant="ghost"
              onClick={() => {
                setActiveItem(null);
                setOpenPopup(false);
              }}
            />
          </div>
        </div>

        <div className="mt-3 mb-4 w-full px-4">
          <FroalaTextareaView value={x.text} />
        </div>

        <div className="flex w-full border-t px-4 pt-4">
          <div className="ml-auto flex flex-row items-center gap-3">
            <IconButton
              className={
                index === 0 ? '!bg-grey-01 !text-grey-04' : '!text-grey-07'
              }
              size="sm"
              Icon={ArrowLeft}
              variant="ghost"
              disabled={index === 0}
              onClick={() => setActiveItem(labels[index - 1])}
            />
            <IconButton
              className={
                labels?.length === index + 1
                  ? '!bg-grey-01 !text-grey-04'
                  : '!text-grey-07'
              }
              size="sm"
              Icon={ArrowRight}
              variant="ghost"
              disabled={labels?.length === index + 1}
              onClick={() => setActiveItem(labels[index + 1])}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ImageShowPartsView = ({
  state,
  index,
  onModified = undefined,
}) => {
  const { t } = useTranslation();

  const [openPopup, setOpenPopup] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const triggerRefs = useRef(new Map());
  const popupRefs = useRef(new Map());

  const registerTrigger = (uid, el) => {
    if (el) triggerRefs.current.set(uid, el);
    else triggerRefs.current.delete(uid);
  };

  const registerPopup = (uid, el) => {
    if (el) popupRefs.current.set(uid, el);
    else popupRefs.current.delete(uid);
  };

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!openPopup || !activeItem?.uid) return;

      const uid = activeItem.uid;
      const triggerEl = triggerRefs.current.get(uid);
      const popupEl = popupRefs.current.get(uid);

      const path = (e.composedPath && e.composedPath()) || [];

      const inTrigger =
        triggerEl && (triggerEl.contains(e.target) || path.includes(triggerEl));
      const inPopup =
        popupEl && (popupEl.contains(e.target) || path.includes(popupEl));

      if (!inTrigger && !inPopup) {
        setActiveItem(null);
        setOpenPopup(false);
      }
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [openPopup, activeItem]);

  const onLoadImg = () => {
    const canvas = document.getElementById(state.uid + '_canvas');
    const img = document.getElementById(state.uid + '_img');
    canvas.width = img.width * 5;
    canvas.height = img.height * 5;

    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 15;
    state.labels.forEach((x, index) => {
      ctx.beginPath();
      const startX = x.tx * canvas.width;
      const startY = x.ty * canvas.height;
      const endX = x.x * canvas.width;
      const endY = x.y * canvas.height;
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = pastels[index];
      ctx.stroke();
      ctx.closePath();
    });
  };

  return (
    <div
      className={`flex flex-col gap-4 rounded-lg bg-[#f8f8f9] p-4`}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
    >
      <FroalaTextareaView value={state.title} />

      <div>
        <div
          style={{ marginBottom: 30, position: 'relative' }}
          className="pane LinkWidget widget-item"
          id={state.uid + '_view'}
        >
          <div id={state.uid + '_view_container'}>
            <div className="relative">
              <img
                id={state.uid + '_img'}
                src={state.image}
                onLoad={onLoadImg}
                style={{
                  width: '100%',
                  cursor: 'inherit',
                  pointerEvents: 'none',
                }}
              />
              <canvas
                id={state.uid + '_canvas'}
                style={{
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                className="absolute"
              ></canvas>
              <div className={`block`}>
                {state.labels.map((x, index) => {
                  const isThisOpen = openPopup && activeItem?.uid === x?.uid;
                  return (
                    <div key={state.labels.text + '_' + index}>
                      <span
                        className="absolute h-3.5 w-3.5 rounded-full border-[3px]"
                        style={{
                          transform: 'translate(-50%, -50%)',
                          top: x.ty * 100 + '%',
                          left: x.tx * 100 + '%',
                          borderColor: `${pastels[index]}`,
                        }}
                      ></span>

                      <div
                        id={x.uid + '_view'}
                        style={{
                          top: x.y * 100 + '%',
                          left: x.x * 100 + '%',
                          transform: 'translate(-11%, -50%)',
                        }}
                        className={`absolute ${
                          x?.uid === activeItem?.uid ? 'z-[2]' : 'z-[1]'
                        } rounded-md ${
                          x?.uid === activeItem?.uid
                            ? 'border-[2px] border-secondary-04 bg-white'
                            : 'border-[2px] border-transparent'
                        }`}
                      >
                        <div
                          key={state.labels.text + '_label'}
                          className={`rounded-md ${
                            x?.uid === activeItem?.uid
                              ? 'border-[2px] border-white'
                              : 'border-[2px] border-transparent'
                          }`}
                        >
                          {state?.interactiveType ? (
                            <div
                              ref={(el) => registerTrigger(x.uid, el)}
                              style={{ backgroundColor: pastels[index] }}
                              className="cursor-pointer rounded-md px-2.5 pb-1 pt-0.5 text-white"
                              onClick={() => {
                                if (isThisOpen) {
                                  setActiveItem(null);
                                  setOpenPopup(false);
                                } else {
                                  setActiveItem(x);
                                  setOpenPopup(true);
                                }
                              }}
                            >
                              {getCharForNumber(index)}
                            </div>
                          ) : (
                            <div
                              style={{
                                backgroundColor: `${pastels[index]}`,
                              }}
                              className={`mr-2 rounded-md px-2.5 pb-1 pt-0.5 text-white`}
                            >
                              {getCharForNumber(index)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {state?.interactiveType && (
                <PopupInfo
                  pastels={pastels}
                  labels={state.labels}
                  openPopup={openPopup}
                  setOpenPopup={setOpenPopup}
                  activeItem={activeItem}
                  setActiveItem={setActiveItem}
                  registerPopup={registerPopup}
                  getCharForNumber={getCharForNumber}
                />
              )}
            </div>
            {!state?.interactiveType && (
              <div className="mt-3 flex w-full flex-col gap-2">
                {state?.labels?.map((el, index) => {
                  const htmlText = el?.text.replace(/\n/g, '<br />');
                  return (
                    <div
                      key={el.text + '_' + index}
                      className="flex flex-row items-center "
                    >
                      <div
                        className="mr-2 rounded-md px-2.5 pb-1 pt-0.5 text-white"
                        style={{
                          backgroundColor: `${pastels[index]}`,
                        }}
                      >
                        {getCharForNumber(index)}
                      </div>
                      <div
                        dangerouslySetInnerHTML={{ __html: htmlText }}
                        className="w-full rounded-lg bg-white p-2"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
